import { RIYAL } from "../lib/invoicePrint";
import { SaudiRiyal } from "../../components/SaudiRiyal";
import { useEffect, useState, Fragment } from "react";
import { Loader2, Table2, FileSpreadsheet, Printer } from "lucide-react";
import { toast } from "sonner";
import { finApi, money, apiErr } from "../api";
import { PageHeader, Card } from "../FinanceApp";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const today = () => new Date().toISOString().slice(0, 10);
const yearStart = () => new Date().toISOString().slice(0, 4) + "-01-01";
const amt = (v) => (v ? money(v) : "");

export default function FinAmericanJournal() {
  const [range, setRange] = useState({ from_date: yearStart(), to_date: today() });
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setD(await finApi.americanJournal(range)); } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const accs = d?.accounts || [];

  const exportExcel = async () => {
    setExporting(true);
    try {
      const blob = await finApi.americanJournalExport(range);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `american_journal_${range.from_date}_${range.to_date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تصدير ملف Excel");
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(false); }
  };

  const printPdf = () => {
    if (!d || !d.rows.length) { toast.warning("لا توجد بيانات للطباعة"); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const head2 = accs.map(() => "<th>مدين</th><th>دائن</th>").join("");
    const head1 = accs.map((a) => `<th colspan="2">${esc(a.code)} · ${esc(a.name)}</th>`).join("");
    const body = d.rows.map((r) => {
      const cells = accs.map((a) => {
        const c = r.cells[a.id] || {};
        return `<td class="num">${c.debit ? money(c.debit) : ""}</td><td class="num">${c.credit ? money(c.credit) : ""}</td>`;
      }).join("");
      return `<tr><td>${esc(r.date)}</td><td>${esc(r.number)}</td><td class="desc">${esc(r.description || "—")}</td>${cells}</tr>`;
    }).join("");
    const totals = accs.map((a) => {
      const t = d.totals[a.id] || {};
      return `<td class="num">${t.debit ? money(t.debit) : ""}</td><td class="num">${t.credit ? money(t.credit) : ""}</td>`;
    }).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>الدفتر اليومي الأمريكي</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family:SaudiRiyal,'IBM Plex Sans Arabic','Arial',sans-serif; color:#0f172a; }
        h1 { font-size:16px; margin:0 0 2px; text-align:center; }
        .period { text-align:center; font-size:12px; color:#475569; margin-bottom:10px; }
        table { width:100%; border-collapse:collapse; font-size:10px; }
        th,td { border:1px solid #cbd5e1; padding:3px 4px; text-align:center; }
        thead th { background:#0f172a; color:#fff; }
        td.desc { text-align:right; }
        td.num { direction:ltr; }
        tfoot td { background:#e2e8f0; font-weight:bold; }
        .grand { margin-top:10px; font-weight:bold; text-align:center; font-size:12px; }
      </style></head><body>
      <h1>الدفتر اليومي الأمريكي</h1>
      <div class="period">من ${esc(d.from_date || "—")} إلى ${esc(d.to_date || "—")}</div>
      <table><thead>
        <tr><th rowspan="2">التاريخ</th><th rowspan="2">رقم القيد</th><th rowspan="2">البيان</th>${head1}</tr>
        <tr>${head2}</tr>
      </thead><tbody>${body}</tbody>
      <tfoot><tr><td colspan="3">إجمالي الأعمدة (أرصدة الأستاذ)</td>${totals}</tr></tfoot>
      </table>
      <div class="grand">إجمالي المدين: ${money(d.grand.debit)}${RIYAL} = إجمالي الدائن: ${money(d.grand.credit)}${RIYAL}</div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("يرجى السماح بالنوافذ المنبثقة للطباعة"); return; }
    w.document.write(html); w.document.close();
  };

  return (
    <div data-testid="fin-american-journal">
      <PageHeader title="الدفتر اليومي الأمريكي" subtitle="اليومية الأمريكية — صف واحد لكل قيد وعمودا مدين/دائن لكل حساب مع إجماليات الأعمدة" />
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="space-y-1"><Label className="text-xs">من</Label><Input type="date" value={range.from_date} onChange={(e) => setRange({ ...range, from_date: e.target.value })} className="w-40" data-testid="aj-from" /></div>
        <div className="space-y-1"><Label className="text-xs">إلى</Label><Input type="date" value={range.to_date} onChange={(e) => setRange({ ...range, to_date: e.target.value })} className="w-40" data-testid="aj-to" /></div>
        <Button onClick={load} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="aj-apply"><Table2 className="h-4 w-4" /> عرض</Button>
        <div className="flex-1" />
        <Button onClick={exportExcel} disabled={exporting || !d?.rows?.length} variant="outline" className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="aj-export-excel">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} تصدير Excel
        </Button>
        <Button onClick={printPdf} disabled={!d?.rows?.length} variant="outline" className="gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="aj-print-pdf">
          <Printer className="h-4 w-4" /> طباعة / PDF
        </Button>
      </div>

      {loading || !d ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div>
        : d.rows.length === 0 ? <Card className="py-16 text-center text-slate-400" data-testid="aj-empty">لا توجد قيود مُرحّلة خلال الفترة</Card> : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-right border-collapse" data-testid="aj-table">
                <thead>
                  <tr className="bg-slate-900 text-white text-xs">
                    <th className="px-2 py-2 font-semibold sticky right-0 bg-slate-900 z-10 whitespace-nowrap">التاريخ</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap">رقم القيد</th>
                    <th className="px-2 py-2 font-semibold whitespace-nowrap min-w-[160px]">البيان</th>
                    {accs.map((a) => (
                      <th key={a.id} colSpan={2} className="px-2 py-1 font-semibold text-center border-r border-slate-700 whitespace-nowrap" title={`${a.code} · ${a.name}`}>{a.name}</th>
                    ))}
                  </tr>
                  <tr className="bg-slate-700 text-slate-100 text-[11px]">
                    <th className="px-2 py-1 sticky right-0 bg-slate-700 z-10"></th>
                    <th className="px-2 py-1"></th>
                    <th className="px-2 py-1"></th>
                    {accs.map((a) => (
                      <Fragment key={a.id}>
                        <th className="px-2 py-1 font-semibold text-center border-r border-slate-600 whitespace-nowrap">مدين</th>
                        <th className="px-2 py-1 font-semibold text-center whitespace-nowrap">دائن</th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {d.rows.map((r) => (
                    <tr key={r.id} data-testid={`aj-row-${r.number}`} className={`border-t border-slate-100 hover:bg-slate-50 ${r.status === "reversed" ? "bg-amber-50/40" : ""}`}>
                      <td className="px-2 py-2 text-slate-500 sticky right-0 bg-white z-10 whitespace-nowrap">{r.date}</td>
                      <td className="px-2 py-2 font-semibold text-slate-800 whitespace-nowrap">{r.number}</td>
                      <td className="px-2 py-2 font-sans text-slate-600 min-w-[160px]">{r.description || "—"}</td>
                      {accs.map((a) => {
                        const c = r.cells[a.id] || {};
                        return (
                          <Fragment key={`${r.id}-${a.id}`}>
                            <td className="px-2 py-2 text-center border-r border-slate-100 text-emerald-700" dir="ltr">{amt(c.debit)}</td>
                            <td className="px-2 py-2 text-center text-rose-700" dir="ltr">{amt(c.credit)}</td>
                          </Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="tabular-nums">
                  <tr className="bg-slate-100 font-bold border-t-2 border-slate-800">
                    <td className="px-2 py-2 sticky right-0 bg-slate-100 z-10 font-sans" colSpan={3}>إجمالي الأعمدة (أرصدة الأستاذ)</td>
                    {accs.map((a) => {
                      const t = d.totals[a.id] || {};
                      return (
                        <Fragment key={`t-${a.id}`}>
                          <td className="px-2 py-2 text-center border-r border-slate-300" dir="ltr">{amt(t.debit)}</td>
                          <td className="px-2 py-2 text-center" dir="ltr">{amt(t.credit)}</td>
                        </Fragment>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-slate-100 text-sm">
              <span className="text-slate-500">عدد القيود: <span className="font-semibold text-slate-700 tabular-nums">{d.rows.length}</span></span>
              <span className={`font-semibold ${d.grand.debit === d.grand.credit ? "text-emerald-600" : "text-rose-600"}`} data-testid="aj-grand">
                إجمالي المدين <span className="tabular-nums" dir="ltr">{money(d.grand.debit)} <SaudiRiyal /></span> = إجمالي الدائن <span className="tabular-nums" dir="ltr">{money(d.grand.credit)} <SaudiRiyal /></span>
              </span>
            </div>
          </Card>
        )}
    </div>
  );
}
